import React from 'react';
import {
  Flex,
  Heading,
  Button,
  Text,
  Spinner,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
} from '@chatsdet/ui';
import { SealCheck, RocketLaunch, Check } from '@phosphor-icons/react';
import type { WorkflowReviewProgressEvent, WorkflowReviewResult } from '@chatsdet/types';
import type { PendingBranchPaste } from '../hooks/useClipboard';
import type { RequestReviewHandlers } from '../hooks/useReviewWorkflow';
import { ReviewScorecard } from './ReviewScorecard';
import { DeactivateNodeDialog } from './DeactivateNodeDialog';

type ReviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'done'; result: WorkflowReviewResult };

/** Live activity feed shown while the review agent runs. */
const ReviewActivity: React.FC<{ steps: string[]; thinking: string }> = ({ steps, thinking }) => (
  <Flex direction="column" gap="xs" className="py-1">
    {steps.map((step, i) => {
      const isLast = i === steps.length - 1;
      return (
        <Flex key={`${i}-${step}`} align="center" gap="sm">
          {isLast ? (
            <Spinner className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <Check size={14} weight="bold" className="text-emerald-500" />
          )}
          <Text className={`text-sm ${isLast ? '' : 'text-muted-foreground'}`}>{step}</Text>
        </Flex>
      );
    })}
    {thinking && (
      <Text className="mt-1 line-clamp-2 pl-6 text-xs italic text-muted-foreground/80">
        {thinking}
      </Text>
    )}
  </Flex>
);

/**
 * Approval dialog with an optional AI-review step.
 *
 * `explain` explains what approval means (a human verified the test checks the
 * right thing — it does NOT by itself put the test into suites) and offers an
 * optional "Request an AI review". `review` runs the review and shows a
 * scorecard + comments (and, when the agent proposed changes, a hand-off to the
 * proposal review). `hooray` celebrates and explains that suite inclusion is now
 * derived: an approved flow joins suites automatically once it has a passing run.
 *
 * Exported for tests; rendered only by FlowDesignerDialogs.
 */
export const ApproveFlowDialog: React.FC<{
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  handleApprove: () => Promise<boolean>;
  isApproving: boolean;
  /** When true, use the eligibility-model copy/flow; otherwise the promotion model. */
  eligibilityModel: boolean;
  /** Eligibility model (flag ON): whether the flow already has a passing run — tailors the post-approval message. */
  hasPassed: boolean;
  /** Promotion model (flag OFF): promote straight from the hooray step. */
  handlePromote: () => Promise<void>;
  isPromoting: boolean;
  readyToPromote: boolean;
  /** Runs the optional AI review, streaming live progress via `onEvent`.
   *  Omitted → the review affordance is hidden. */
  onRequestReview?: (handlers: RequestReviewHandlers) => Promise<WorkflowReviewResult>;
  /** Open the full proposal review for a proposal the review just created. */
  onOpenProposal?: (proposalId: string) => void;
}> = ({
  isOpen,
  setOpen,
  handleApprove,
  isApproving,
  eligibilityModel,
  hasPassed,
  handlePromote,
  isPromoting,
  readyToPromote,
  onRequestReview,
  onOpenProposal,
}) => {
  const [step, setStep] = React.useState<'explain' | 'review' | 'hooray'>('explain');
  const [review, setReview] = React.useState<ReviewState>({ status: 'idle' });
  const [activity, setActivity] = React.useState<string[]>([]);
  const [thinking, setThinking] = React.useState('');

  // Reset to the explainer whenever the dialog is (re)opened.
  React.useEffect(() => {
    if (isOpen) {
      setStep('explain');
      setReview({ status: 'idle' });
      setActivity([]);
      setThinking('');
    }
  }, [isOpen]);

  const runReview = async () => {
    if (!onRequestReview) return;
    setStep('review');
    setReview({ status: 'loading' });
    setActivity([]);
    setThinking('');
    const onEvent = (event: WorkflowReviewProgressEvent) => {
      if (event.type === 'progress') {
        setActivity((prev) => [...prev, event.message]);
      } else if (event.type === 'activity') {
        setActivity((prev) => [...prev, event.label]);
      } else if (event.type === 'text') {
        // Keep a short rolling preview of the model's reasoning.
        setThinking((prev) => (prev + event.delta).slice(-280));
      }
    };
    try {
      const result = await onRequestReview({ onEvent });
      setReview({ status: 'done', result });
    } catch (err) {
      setReview({
        status: 'error',
        message: err instanceof Error ? err.message : 'The review could not be completed.',
      });
    }
  };

  const approveFromReview = async () => {
    const ok = await handleApprove();
    if (ok) setStep('hooray');
  };

  // Widen the modal on the review step so the category gauges sit on one line.
  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setOpen} size={step === 'review' ? '4xl' : '2xl'}>
      {/* Hidden trigger anchor — named distinctly so it never collides with the
          dialog's real "Approve" button by accessible name. */}
      <Button className="hidden">Approve flow dialog</Button>
      <Dialog>
        <DialogContent>
          {step === 'explain' ? (
            <>
              <Heading slot="title">
                <span className="inline-flex items-center gap-2">
                  <SealCheck size={20} weight="fill" style={{ color: '#2f6fed' }} />
                  Approve this test?
                </span>
              </Heading>
              <Text className="text-sm">
                Approving means{' '}
                <strong className="font-semibold">
                  you've verified this test checks the right thing
                </strong>{' '}
                — the steps and assertions match what your team actually wants covered. Other people
                in your organization will see it as a reviewed, trusted spec.
              </Text>
              {eligibilityModel ? (
                <Text className="text-sm text-muted-foreground">
                  Approval doesn't run anything on its own. Once approved, the test joins your
                  suites automatically as soon as it has a passing run.
                </Text>
              ) : (
                <Text className="text-sm text-muted-foreground">
                  Approval doesn't run anything: whether the test runs in suites is a separate step
                  (promote), which you can do right after.
                </Text>
              )}
              <DialogFooter>
                <Button variant="secondary" onPress={() => setOpen(false)}>
                  Cancel
                </Button>
                {onRequestReview && (
                  <Button variant="outline" onPress={runReview}>
                    Request a review
                  </Button>
                )}
                <Button
                  variant="primary"
                  isDisabled={isApproving}
                  onPress={async () => {
                    const ok = await handleApprove();
                    if (ok) {
                      setStep('hooray');
                    }
                  }}
                >
                  {isApproving ? 'Approving…' : 'Approve'}
                </Button>
              </DialogFooter>
            </>
          ) : step === 'review' ? (
            <>
              <Heading slot="title">Review</Heading>

              {review.status === 'loading' && (
                <ReviewActivity
                  steps={activity.length > 0 ? activity : ['Starting review…']}
                  thinking={thinking}
                />
              )}

              {review.status === 'error' && (
                <Text className="text-sm text-muted-foreground">{review.message}</Text>
              )}

              {review.status === 'done' && (
                <>
                  <Text className="text-sm">{review.result.summary}</Text>
                  <ReviewScorecard
                    scorecard={review.result.scorecard}
                    comments={review.result.comments}
                  />
                  {review.result.kind === 'proposed' && (
                    <Text className="text-sm text-muted-foreground">
                      The reviewer proposed{' '}
                      {review.result.comments.length === 1 ? 'a change' : 'changes'} you can apply
                      from the proposal review.
                    </Text>
                  )}
                </>
              )}

              <DialogFooter>
                <Button variant="secondary" onPress={() => setStep('explain')}>
                  Back
                </Button>
                {review.status === 'done' &&
                  review.result.kind === 'proposed' &&
                  onOpenProposal && (
                    <Button
                      variant="primary"
                      onPress={() => {
                        const proposalId =
                          review.result.kind === 'proposed' ? review.result.proposalId : null;
                        if (proposalId) onOpenProposal(proposalId);
                      }}
                    >
                      Review proposal
                    </Button>
                  )}
                {review.status !== 'loading' && (
                  <Button variant="outline" isDisabled={isApproving} onPress={approveFromReview}>
                    {isApproving ? 'Approving…' : 'Approve anyway'}
                  </Button>
                )}
              </DialogFooter>
            </>
          ) : (
            <>
              <Heading slot="title">
                <span className="inline-flex items-center gap-2">
                  <RocketLaunch size={20} weight="fill" style={{ color: '#2f6fed' }} />
                  Hooray — approved!
                </span>
              </Heading>
              {eligibilityModel ? (
                <>
                  <Text className="text-sm">This test is now marked as approved.</Text>
                  {hasPassed ? (
                    <Text className="text-sm text-muted-foreground">
                      It's eligible and will run whenever your test suites run — no extra step.
                    </Text>
                  ) : (
                    <Text className="text-sm text-muted-foreground">
                      It'll join your test suites automatically once it has a passing run.
                    </Text>
                  )}
                  <DialogFooter>
                    <Button variant="primary" onPress={() => setOpen(false)}>
                      Done
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <Text className="text-sm">
                    This test is now marked as approved. Want to promote it so it runs whenever your
                    test suites run?
                  </Text>
                  {!readyToPromote && (
                    <Text className="text-sm text-muted-foreground">
                      This flow doesn't have a passing run yet — run it first, then promote it from
                      the flow header.
                    </Text>
                  )}
                  <DialogFooter>
                    <Button variant="secondary" onPress={() => setOpen(false)}>
                      Skip for now
                    </Button>
                    {readyToPromote && (
                      <Button
                        variant="primary"
                        isDisabled={isPromoting}
                        onPress={async () => {
                          await handlePromote();
                          setOpen(false);
                        }}
                      >
                        {isPromoting ? 'Promoting…' : 'Promote now'}
                      </Button>
                    )}
                  </DialogFooter>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </DialogTrigger>
  );
};

type FlowDesignerDialogsProps = {
  // Archive workflow dialog (soft-delete)
  isArchiveDialogOpen: boolean;
  setIsArchiveDialogOpen: (open: boolean) => void;
  workflowToArchive: { id: string; name: string } | null;
  setWorkflowToArchive: (wf: { id: string; name: string } | null) => void;
  archiveWorkflowMutation: {
    mutate: (params: { id: string; archivedFrom?: string; confirmationMethod?: string }) => void;
    isPending: boolean;
  };

  // Wrong org dialog
  wrongOrgInfo: { orgId: string; orgName: string } | null;
  setWrongOrgInfo: (info: { orgId: string; orgName: string } | null) => void;
  switchOrg: (orgId: string, redirectPath: string) => void;
  isSwitchingOrg: boolean;
  workflowId: string | null;
  navigate: (opts: any) => void;

  // Two-step approve dialog (explain → hooray)
  isApproveDialogOpen: boolean;
  setApproveDialogOpen: (open: boolean) => void;
  /** Performs the approval; resolves true on success so the dialog advances to the hooray step. */
  handleApprove: () => Promise<boolean>;
  isApproving: boolean;
  /** When true, use the eligibility-model copy/flow; otherwise the promotion model. */
  eligibilityModel: boolean;
  /** Eligibility model (flag ON): whether the flow already has a passing run — tailors the post-approval message. */
  hasPassed: boolean;
  /** Promotion model (flag OFF): promote straight from the hooray step. */
  handlePromote: () => Promise<void>;
  isPromoting: boolean;
  /** Run gate — "Promote now" is offered only when a passing run validates the flow. */
  readyToPromote: boolean;
  /** Runs the optional in-modal AI review (streams live progress via `onEvent`).
   *  Omitted → the affordance is hidden. */
  onRequestReview?: (handlers: RequestReviewHandlers) => Promise<WorkflowReviewResult>;
  /** Hand off to the proposal review for a proposal the AI review created. */
  onOpenProposal?: (proposalId: string) => void;

  // Force promote dialog (promotion model / flag OFF)
  isForcePromoteDialogOpen: boolean;
  setForcePromoteDialogOpen: (open: boolean) => void;
  handleForcePromote: () => Promise<void>;

  // Optimize (cache convergence agent) confirmation dialog
  isOptimizeDialogOpen: boolean;
  setOptimizeDialogOpen: (open: boolean) => void;
  handleConfirmOptimize: () => Promise<void>;

  // Branch paste dialog
  pendingBranchPaste: PendingBranchPaste | null;
  onConfirmBranchPaste: (edgeType: string) => void;
  onCancelBranchPaste: () => void;

  // Deactivate/reactivate node dialog (captures an optional reason, posted as
  // a node-anchored comment)
  pendingDeactivateNode: { nodeId: string; next: boolean; label: string } | null;
  onDeactivateDialogOpenChange: (open: boolean) => void;
  onConfirmDeactivateNode: (reason: string) => void;
};

export const FlowDesignerDialogs: React.FC<FlowDesignerDialogsProps> = ({
  isArchiveDialogOpen,
  setIsArchiveDialogOpen,
  workflowToArchive,
  setWorkflowToArchive,
  archiveWorkflowMutation,
  wrongOrgInfo,
  setWrongOrgInfo,
  switchOrg,
  isSwitchingOrg,
  workflowId,
  navigate,
  isApproveDialogOpen,
  setApproveDialogOpen,
  handleApprove,
  isApproving,
  eligibilityModel,
  hasPassed,
  handlePromote,
  isPromoting,
  readyToPromote,
  onRequestReview,
  onOpenProposal,
  isForcePromoteDialogOpen,
  setForcePromoteDialogOpen,
  handleForcePromote,
  isOptimizeDialogOpen,
  setOptimizeDialogOpen,
  handleConfirmOptimize,
  pendingBranchPaste,
  onConfirmBranchPaste,
  onCancelBranchPaste,
  pendingDeactivateNode,
  onDeactivateDialogOpenChange,
  onConfirmDeactivateNode,
}) => (
  <>
    {/* Archive workflow confirmation dialog */}
    <DialogTrigger isOpen={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
      <Button className="hidden">Archive</Button>
      <Dialog
        title="Archive Workflow?"
        description="Archived flows are hidden from lists and won't run in test suites. You can restore them later."
      >
        <DialogContent>
          <Text className="text-sm">
            Archive <strong className="font-semibold">{workflowToArchive?.name}</strong>?
          </Text>
        </DialogContent>
        <DialogFooter>
          <Flex gap="sm" justify="end">
            <Button variant="outline" onPress={() => setIsArchiveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onPress={() => {
                if (workflowToArchive) {
                  archiveWorkflowMutation.mutate({
                    id: workflowToArchive.id,
                    archivedFrom: 'editor',
                    confirmationMethod: 'modal_confirm',
                  });
                  setIsArchiveDialogOpen(false);
                  setWorkflowToArchive(null);
                }
              }}
              isDisabled={archiveWorkflowMutation.isPending}
            >
              {archiveWorkflowMutation.isPending ? 'Archiving...' : 'Archive'}
            </Button>
          </Flex>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>

    {/* Wrong organization dialog */}
    <DialogTrigger
      isOpen={wrongOrgInfo !== null}
      onOpenChange={(open) => !open && setWrongOrgInfo(null)}
    >
      <Button className="hidden">Switch Org</Button>
      <Dialog
        title="Flow in Different Organization"
        description="Switch organizations to access this flow"
      >
        <DialogContent>
          <Text className="text-sm">
            This flow belongs to <strong className="font-semibold">{wrongOrgInfo?.orgName}</strong>.
            Would you like to switch to that organization to access it?
          </Text>
        </DialogContent>
        <DialogFooter>
          <Flex gap="sm" justify="end">
            <Button
              variant="outline"
              onPress={() => {
                setWrongOrgInfo(null);
                navigate({ to: '/flows' });
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={() => {
                if (wrongOrgInfo) {
                  switchOrg(wrongOrgInfo.orgId, `/flows/${workflowId}`);
                }
              }}
              isDisabled={isSwitchingOrg}
            >
              {isSwitchingOrg ? 'Switching...' : 'Switch Organization'}
            </Button>
          </Flex>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>

    {/* Two-step approve dialog */}
    <ApproveFlowDialog
      isOpen={isApproveDialogOpen}
      setOpen={setApproveDialogOpen}
      handleApprove={handleApprove}
      isApproving={isApproving}
      eligibilityModel={eligibilityModel}
      hasPassed={hasPassed}
      handlePromote={handlePromote}
      isPromoting={isPromoting}
      readyToPromote={readyToPromote}
      onRequestReview={onRequestReview}
      onOpenProposal={onOpenProposal}
    />

    {/* Force promote dialog (promotion model / flag OFF) */}
    {!eligibilityModel && (
      <DialogTrigger isOpen={isForcePromoteDialogOpen} onOpenChange={setForcePromoteDialogOpen}>
        <Button className="hidden">Force Promote</Button>
        <Dialog>
          <DialogContent>
            <Heading slot="title">Force Promote</Heading>
            <Text>
              This flow has changes that have not been validated by a test run. Promoting may cause
              test suites that include this flow to fail.
            </Text>
            <DialogFooter>
              <Button variant="secondary" onPress={() => setForcePromoteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={async () => {
                  setForcePromoteDialogOpen(false);
                  await handleForcePromote();
                }}
              >
                Force Promote
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogTrigger>
    )}

    {/* Optimize (cache convergence agent) confirmation dialog */}
    <DialogTrigger isOpen={isOptimizeDialogOpen} onOpenChange={setOptimizeDialogOpen}>
      <Button className="hidden">Optimize</Button>
      <Dialog>
        <DialogContent>
          <Heading slot="title">Optimize this flow?</Heading>
          <Text>
            This starts repeated cache-only test runs in a shadow copy. We’ll promote only changes
            proven by clean replays; your current flow stays unchanged while optimization runs.
          </Text>
          <DialogFooter>
            <Button variant="secondary" onPress={() => setOptimizeDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={async () => {
                setOptimizeDialogOpen(false);
                await handleConfirmOptimize();
              }}
            >
              Optimize
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DialogTrigger>

    {/* Paste into branch dialog */}
    <DialogTrigger
      isOpen={pendingBranchPaste !== null}
      onOpenChange={(open) => !open && onCancelBranchPaste()}
    >
      <Button className="hidden">Branch Paste</Button>
      <Dialog>
        <DialogContent>
          <Heading slot="title">Paste into which branch?</Heading>
          <Text className="text-sm">
            The selected node has multiple branches. Choose where to paste the copied nodes.
          </Text>
          <DialogFooter>
            <Flex gap="sm" justify="end">
              <Button variant="outline" onPress={onCancelBranchPaste}>
                Cancel
              </Button>
              <Button variant="secondary" onPress={() => onConfirmBranchPaste('condition_false')}>
                No branch
              </Button>
              <Button variant="primary" onPress={() => onConfirmBranchPaste('condition_true')}>
                Yes branch
              </Button>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DialogTrigger>

    {/* Deactivate/reactivate node dialog */}
    <DeactivateNodeDialog
      isOpen={pendingDeactivateNode !== null}
      onOpenChange={onDeactivateDialogOpenChange}
      next={pendingDeactivateNode?.next ?? true}
      nodeLabel={pendingDeactivateNode?.label ?? ''}
      onConfirm={onConfirmDeactivateNode}
    />
  </>
);
